// IconButton — icon-only action. `outline` is a bordered square-rounded
// button; `ghost` is borderless. `aria-label` is required for a11y.

import { forwardRef, type ButtonHTMLAttributes } from 'react';
import Icon from './Icon';

export type IconButtonVariant = 'outline' | 'ghost';
export type IconButtonSize = 'sm' | 'md' | 'lg';

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Kebab-case Icon name. */
  icon: string;
  variant?: IconButtonVariant;
  size?: IconButtonSize;
  /** Accessible name — required since there is no visible label. */
  'aria-label': string;
}

const ICON_SIZE: Record<IconButtonSize, number> = { sm: 16, md: 18, lg: 20 };

const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { icon, variant = 'outline', size = 'md', type = 'button', className = '', disabled, ...rest },
  ref,
) {
  const classes = ['app-icon-btn', `app-icon-btn--${variant}`, `app-icon-btn--${size}`, className]
    .filter(Boolean)
    .join(' ');

  return (
    <button ref={ref} type={type} className={classes} disabled={disabled} {...rest}>
      <Icon name={icon} size={ICON_SIZE[size]} aria-hidden='true' />
    </button>
  );
});

export default IconButton;
